import React, { useState, useEffect } from 'react';
import { Container, Spinner, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import apiClient from '../../../utils/axios';
import { FaPause, FaPlay, FaComment, FaImage, FaVideo, FaMicrophone, FaCrown } from 'react-icons/fa';
import './YearInReviewPage.css';

const YearInReviewPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [year] = useState(2025);

    const groupDisplayNames = {
        nogizaka: '乃木坂46',
        hinatazaka: '日向坂46',
        sakurazaka: '櫻坂46',
    };

    const slides = [
        'intro',
        'total_messages',
        'message_types',
        'monthly_activity',
        'top_overall',
        'group_leaders',
        'rising_stars',
        'hourly_activity',
        'summary'
    ];

    useEffect(() => {
        document.title = `${year} 年度回顧`;

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get(`/stats/yearly?year=${year}`);
                const apiData = response.data;

                // Map monthly data
                const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
                const monthlyStats = monthNames.map((name, index) => {
                    const found = apiData.monthly_activity?.find(m => m.month === (index + 1));
                    return { name, count: found ? found.count : 0 };
                });

                // Map hourly data
                const bins = [
                    { label: '0-4', start: 0, end: 3 },
                    { label: '4-8', start: 4, end: 7 },
                    { label: '8-12', start: 8, end: 11 },
                    { label: '12-16', start: 12, end: 15 },
                    { label: '16-20', start: 16, end: 19 },
                    { label: '20-24', start: 20, end: 23 }
                ];

                const hourlyStats = bins.map(bin => {
                    const count = apiData.hourly_activity?.filter(h => h.hour >= bin.start && h.hour <= bin.end)
                        .reduce((sum, h) => sum + h.count, 0) || 0;
                    return { hour: bin.label, count: count };
                });

                setData({
                    year: apiData.year,
                    totalMessages: apiData.total_messages,
                    activeDays: apiData.active_days_count,
                    mostActiveMonth: apiData.most_active_month,
                    rankings: apiData.rankings,
                    monthlyStats: monthlyStats,
                    hourlyStats: hourlyStats,
                    messageTypes: apiData.message_type_breakdown
                });
            } catch (error) {
                console.error("Failed to fetch year in review data", error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [year]);

    // Interaction States
    const [isPaused, setIsPaused] = useState(false);
    const [manualPause, setManualPause] = useState(false);
    const [longPressTimer, setLongPressTimer] = useState(null);
    const [lastTapTime, setLastTapTime] = useState(0);

    const handleAnimationEnd = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(curr => curr + 1);
        }
    };

    const goToNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(curr => curr + 1);
        } else {
            navigate('/stats');
        }
    };

    const goToPrev = () => {
        if (currentSlide > 0) {
            setCurrentSlide(curr => curr - 1);
        }
    };

    const togglePause = (e) => {
        e.stopPropagation(); // Prevent triggering navigation
        setManualPause(!manualPause);
    };

    // Interaction Handlers
    const handlePointerDown = () => {
        // Start long press timer
        const timer = setTimeout(() => {
            setIsPaused(true);
        }, 200);
        setLongPressTimer(timer);
        setLastTapTime(Date.now());
    };

    const handlePointerUp = (e) => {
        if (longPressTimer) clearTimeout(longPressTimer);
        setIsPaused(false);

        // If manual pause is active, touches shouldn't resume playback (logic choice: manual overrides everything)
        // But touches for navigation should still work? The user might want to manually navigate even while paused.
        // Let's allow manual navigation even if paused.

        const pressDuration = Date.now() - lastTapTime;

        if (pressDuration < 200) {
            const width = window.innerWidth;
            const x = e.clientX || (e.changedTouches && e.changedTouches[0].clientX);

            // Ignore clicks properly if they hit controls (handled by stopPropagation on button)

            if (x < width * 0.3) {
                goToPrev();
            } else {
                goToNext();
            }
        }
    };

    // Helper component for rendering ranking list
    const RankingList = ({ items, limit = 5 }) => (
        <div className="yir-ranking-list">
            {items.slice(0, limit).map((item, index) => (
                <div key={index} className={`yir-ranking-item yir-rank-${index + 1} group-${item.group || 'default'}`}>
                    <div className="yir-rank-number">{index + 1}</div>
                    <div className="yir-member-info">
                        <div className="yir-member-name">{item.name}</div>
                        <div className="yir-member-group">
                            {groupDisplayNames[item.group]}
                            {item.term ? ` ${item.term}期` : ''}
                        </div>
                    </div>
                    <div className="yir-member-count">{item.count}</div>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="yir-container">
                <Spinner animation="border" variant="light" />
                <p className="mt-3 text-white">正在整理您的 {year} 年回顧...</p>
            </div>
        );
    }

    if (!data) return <div className="yir-container">無法載入數據</div>;

    const renderSlideContent = () => {
        switch (slides[currentSlide]) {
            case 'intro':
                return (
                    <div className="yir-slide">
                        <h1 className="yir-title">2025<br />年度回顧</h1>
                        <p className="yir-stat-label">準備好看看你的這一年了嗎？</p>
                        <Button className="yir-btn yir-btn-primary mt-4" onClick={goToNext}>開始回顧</Button>
                    </div>
                );

            case 'total_messages':
                return (
                    <div className="yir-slide">
                        <h2 className="yir-stat-label">今年你收到了</h2>
                        <div className="yir-stat-big">{data.totalMessages.toLocaleString()}</div>
                        <p className="yir-stat-label">則來自成員的訊息</p>
                        <div className="mt-4 text-white-50">
                        </div>
                    </div>
                );

            case 'message_types':
                return (
                    <div className="yir-slide yir-slide-medium">
                        <h2 className="yir-title">訊息類型</h2>
                        <div className="yir-groups-grid yir-grid-2x2">
                            {['text', 'image', 'video', 'audio'].map(type => {
                                const typeData = data.messageTypes[type];
                                if (!typeData) return null;
                                const label = { text: '文字', image: '圖片', video: '影片', audio: '語音' }[type];
                                const Icon = { text: FaComment, image: FaImage, video: FaVideo, audio: FaMicrophone }[type];
                                const color = { text: '#fff', image: '#ff00cc', video: '#333399', audio: '#00c6ff' }[type];

                                return (
                                    <div className="yir-group-card" key={type}>
                                        <div className="yir-type-icon" style={{ color: color }}><Icon /></div>
                                        <h3 className="yir-type-label">{label}</h3>
                                        <div className="yir-stat-big yir-type-count">{typeData.count.toLocaleString()}</div>

                                        <div className="mt-3 text-start px-3">
                                            {typeData.top_members && typeData.top_members.slice(0, 3).map((member, idx) => (
                                                <div key={idx} className={`d-flex align-items-center justify-content-between ${idx === 0 ? 'mb-2 yir-type-member-item-first' : 'mb-1 yir-type-member-item'}`}>
                                                    <div className="d-flex align-items-center">
                                                        {idx === 0 && <FaCrown className="text-warning me-2" />}
                                                        <span>{member.name}</span>
                                                    </div>
                                                    <span>{member.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case 'monthly_activity':
                return (
                    <div className="yir-slide yir-slide-narrow">
                        <h2 className="yir-title">訊息高峰月</h2>
                        <p className="text-white mb-4">
                            <span className="text-warning display-6">{data.mostActiveMonth.month}月</span> 是最熱鬧的月份，
                            這期間你總共收到了 {data.mostActiveMonth.count} 則訊息。
                        </p>
                        <div className="yir-chart-container yir-chart-fixed-height">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.monthlyStats}>
                                    <XAxis dataKey="name" stroke="#fff" tick={{ fill: '#fff' }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f0c29', border: 'none', borderRadius: '10px' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="count" fill="#8884d8">
                                        {data.monthlyStats.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.count === data.mostActiveMonth.count ? '#ff00cc' : '#333399'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'top_overall':
                return (
                    <div className="yir-slide">
                        <h2 className="yir-title">最活躍成員排行</h2>
                        <p className="yir-stat-label mb-4">這些成員今年傳送了最多訊息給你</p>
                        <RankingList items={data.rankings.overall} limit={10} />
                    </div>
                );

            case 'group_leaders':
                return (
                    <div className="yir-slide yir-slide-wide">
                        <h2 className="yir-title mb-5">各團訊息Top</h2>
                        <div className="yir-groups-grid">
                            <div className="yir-group-card">
                                <h3 className="yir-group-heading-nogizaka">乃木坂46</h3>
                                <RankingList items={data.rankings.groups.nogizaka.map(i => ({ ...i, group: 'nogizaka' }))} limit={3} />
                            </div>
                            <div className="yir-group-card">
                                <h3 className="yir-group-heading-hinatazaka">日向坂46</h3>
                                <RankingList items={data.rankings.groups.hinatazaka.map(i => ({ ...i, group: 'hinatazaka' }))} limit={3} />
                            </div>
                            <div className="yir-group-card">
                                <h3 className="yir-group-heading-sakurazaka">櫻坂46</h3>
                                <RankingList items={data.rankings.groups.sakurazaka.map(i => ({ ...i, group: 'sakurazaka' }))} limit={3} />
                            </div>
                        </div>
                    </div>
                );

            case 'rising_stars':
                return (
                    <div className="yir-slide">
                        <h2 className="yir-title">注目新星</h2>
                        <p className="yir-stat-label mb-4">今年新成員中傳送訊息最勤奮的成員</p>
                        <RankingList items={data.rankings.new_members} limit={10} />
                    </div>
                );

            case 'hourly_activity':
                return (
                    <div className="yir-slide yir-slide-narrow">
                        <h2 className="yir-title">發信時段分佈</h2>
                        <p className="yir-stat-label mb-4">成員們最常選在這些時段傳訊息給你</p>
                        <div className="yir-chart-container yir-chart-fixed-height">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.hourlyStats}>
                                    <XAxis dataKey="hour" stroke="#fff" />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f0c29', border: 'none', borderRadius: '10px' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="count" fill="#00c6ff" radius={[10, 10, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'summary':
                return (
                    <div className="yir-slide">
                        <h1 className="yir-title">2026</h1>
                        <h2 className="mb-4 text-white">明年繼續支持她們吧！</h2>
                        <Button
                            className="yir-btn yir-btn-primary"
                            onClick={() => navigate('/stats')}
                            onPointerUp={(e) => e.stopPropagation()}
                        >
                            查看詳細統計
                        </Button>
                        <div className="mt-3">
                            <Button
                                variant="link"
                                className="text-white-50"
                                onClick={() => setCurrentSlide(0)}
                                onPointerUp={(e) => e.stopPropagation()}
                            >
                                再看一次
                            </Button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const handlePointerCancel = () => {
        if (longPressTimer) clearTimeout(longPressTimer);
        setIsPaused(false);
    };

    return (
        <div
            className="yir-container"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerCancel}
            onPointerCancel={handlePointerCancel}
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onSelectStart={(e) => e.preventDefault()}
            style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
        >
            {/* Progress Bars */}
            <div className="yir-progress-bar-container">
                {slides.map((_, index) => (
                    <div key={index} className="yir-progress-segment">
                        <div
                            className={`yir-progress-fill ${index < currentSlide ? 'filled' :
                                (index === currentSlide ? 'active' : '')
                                } ${isPaused || manualPause ? 'paused' : ''}`}
                            onAnimationEnd={index === currentSlide ? handleAnimationEnd : undefined}
                        />
                    </div>
                ))}
            </div>

            {/* Play/Pause Button */}
            <div
                className="yir-header-controls"
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
            >
                <button className="yir-icon-btn" onClick={togglePause}>
                    {manualPause ? <FaPlay /> : <FaPause />}
                </button>
            </div>

            {/* Main Content */}
            <Container fluid className="h-100 d-flex align-items-center justify-content-center">
                {renderSlideContent()}
            </Container>
        </div>
    );
};

export default YearInReviewPage;
